import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, ChevronRight, Check, Layers, Package, RefreshCw, Search, User, X,
} from 'lucide-react';
import Highlight from '../Highlight';
import { money } from './clockParse';
import { getProjectTasksCached } from './lookups';
import {
  NodeMark, dupCounts, filterProjectTasks, subtreeCounts, taskKey, tasksByIntermediate,
} from './taskPickerFilter';
import {
  ProjectTaskNode, ProjectTaskRow, ProjectTasksResponse,
} from '../../interfaces/ProjectTasksInterface';
import './TaskPicker.css';

/**
 * Task (piece) picker for the payroll "Add task claim" modal — the returned
 * design bundle `design_handoff_task_claim_picker_design` ported to React.
 *
 * ONE component with TWO browse layouts under one search box (D1): `tree`
 * drills the intermediate hierarchy (WCDP, NPM), `flat` is one searchable list
 * of every task with its breadcrumb (2020PH). Search, row shape, selection,
 * claimants and the keyboard model are identical in both — the tenant flag only
 * changes what sits under the search field.
 *
 * Design decisions this file is the implementation of:
 *  Q1  in-flow bordered panel inside `.pe-mbody`, list scroll region fixed at
 *      266px so loading/empty/error never resize the modal card
 *  Q2  task row grid: selector · mono code · name+second line · claimed pill ·
 *      one money cell ("$1,210.00 of $5,000.00"). No account code on the row.
 *  Q3  `remainingAmount <= 0` rows are greyed inline, never collapsed: filled
 *      check, green tint, the words "Fully claimed", not pickable
 *  Q4  claimants expand INLINE from the "n claimed" pill (the pill is the
 *      disclosure) — a popover would clip inside a scrolling modal body
 *  Q5  reveal-in-place: search sits ABOVE the browse and never replaces it
 *  Q7  while open the panel owns Enter/Esc/arrows (preventDefault +
 *      stopPropagation, as ProjectPicker does) so the shell never saves
 *  Q8  sticky direct/unassigned section header; amber dual-link marker
 *  Q11 under ~520px of panel width the row reflows to two lines
 */

export type TaskPickerMode = 'tree' | 'flat';

export interface TaskPickerProps {
  /** Browse layout — `config.payrollTimesheetEditing.taskPicker`. */
  mode: TaskPickerMode;
  projectID: number | null;
  /** `taskKey` of the picked row, or null while nothing is picked. */
  value: string | null;
  onPick: (row: ProjectTaskRow) => void;
  /** The loaded catalog, so the modal can render the selected summary from it. */
  onLoaded?: (catalog: ProjectTasksResponse) => void;
  /** Carry (Q6): open this path and scroll it into view, selecting NOTHING. */
  expandPathIDs?: number[];
  /** Carry (Q6): mark the row just claimed so the lot reads as "been there". */
  justClaimedKey?: string | null;
  /**
   * Bump to scroll `value`'s row back into view and put the cursor on it. The
   * list is always on screen, so "Show in list" reveals rather than reopens.
   * The initial 0 deliberately does nothing.
   */
  revealToken?: number;
}

type LoadState = 'idle' | 'loading' | 'ok' | 'error';

/** One focusable line in the rendered list, in visual order (the ↑↓ order). */
type RowRef =
  | { kind: 'node'; id: number }
  | { kind: 'task'; key: string; row: ProjectTaskRow; full: boolean };

/** What the render walk produces: a nested plan plus the flat keyboard order. */
type PlanItem =
  | {
      kind: 'node';
      node: ProjectTaskNode;
      mark: NodeMark;
      open: boolean;
      counts: { open: number; total: number };
      children: PlanItem[];
    }
  | { kind: 'task'; row: ProjectTaskRow };

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

const TaskPicker: React.FC<TaskPickerProps> = ({
  mode, projectID, value, onPick, onLoaded, expandPathIDs, justClaimedKey,
  revealToken = 0,
}) => {
  const [catalog, setCatalog] = useState<ProjectTasksResponse | null>(null);
  const [load, setLoad] = useState<LoadState>('idle');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState<Set<number>>(() => new Set(expandPathIDs ?? []));
  const [claimsOpen, setClaimsOpen] = useState<Set<string>>(() => new Set());
  const [hl, setHl] = useState(-1);
  const [attempt, setAttempt] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<RowRef[]>([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (projectID == null) { setCatalog(null); setLoad('idle'); return undefined; }
    setLoad('loading');
    getProjectTasksCached(projectID)
      .then((c) => {
        if (!alive) return;
        setCatalog(c);
        setLoad('ok');
        onLoaded?.(c);
      })
      .catch(() => { if (alive) { setCatalog(null); setLoad('error'); } });
    return () => { alive = false; };
    // `onLoaded` is a render-stable callback in the modal; re-running on its
    // identity would re-fetch on every keystroke of the amount field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID, attempt]);

  // Carry (Q6): a reopen expands the lot just claimed and selects nothing.
  useEffect(() => {
    if (!expandPathIDs?.length) return;
    setOpen((prev) => {
      const next = new Set(prev);
      expandPathIDs.forEach((id) => next.add(id));
      return next;
    });
  }, [expandPathIDs]);

  // The panel owns the keyboard the moment it has something to show (Q7), so
  // it takes focus off the project field once its tasks land — the project is
  // already chosen at that point and the task IS the next step.
  useEffect(() => {
    if (load === 'ok') searchRef.current?.focus();
  }, [load]);

  // Carry (Q6): the restored lot is scrolled into view, not merely expanded.
  const scrolledTo = useRef<number | null>(null);
  useEffect(() => {
    if (load !== 'ok' || !expandPathIDs?.length) return;
    const leaf = expandPathIDs[expandPathIDs.length - 1];
    if (scrolledTo.current === leaf) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-node="${leaf}"]`);
    if (!el) return;
    scrolledTo.current = leaf;
    el.scrollIntoView({ block: 'center' });
  }, [load, expandPathIDs, open]);

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filter = useMemo(() => filterProjectTasks(catalog, term), [catalog, term]);
  const byNode = useMemo(
    () => tasksByIntermediate(catalog?.tasks ?? []),
    [catalog]
  );
  const dups = useMemo(() => dupCounts(catalog?.tasks ?? []), [catalog]);
  const searching = !filter.empty;

  // ── Plan the visible list (and with it the keyboard order) ────────────────
  const { plan, direct, rows } = useMemo(() => {
    const items: PlanItem[] = [];
    const flat: RowRef[] = [];
    if (!catalog) return { plan: items, direct: [] as ProjectTaskRow[], rows: flat };

    const taskVisible = (t: ProjectTaskRow) =>
      filter.empty || filter.visibleTasks.has(taskKey(t));

    const planNode = (node: ProjectTaskNode): PlanItem | null => {
      if (searching && !filter.visibleNodes.has(node.intermediateID)) return null;
      // While a term is live every surviving node is open — a hit must never
      // hide behind a collapsed rail (Q5, "matches auto-expand").
      const isOpen = searching ? true : open.has(node.intermediateID);
      const children: PlanItem[] = [];
      (node.intermediates ?? []).forEach((c) => {
        const p = planNode(c);
        if (p) children.push(p);
      });
      (byNode.get(node.intermediateID) ?? []).forEach((t) => {
        if (taskVisible(t)) children.push({ kind: 'task', row: t });
      });
      return {
        kind: 'node',
        node,
        mark: filter.nodeMarks.get(node.intermediateID) ?? 'plain',
        open: isOpen,
        counts: subtreeCounts(node, byNode),
        children,
      };
    };

    const collect = (item: PlanItem): void => {
      if (item.kind === 'task') {
        flat.push({
          kind: 'task', key: taskKey(item.row), row: item.row,
          full: item.row.remainingAmount <= 0,
        });
        return;
      }
      flat.push({ kind: 'node', id: item.node.intermediateID });
      if (item.open) item.children.forEach(collect);
    };

    if (mode === 'flat') {
      // One list of every task, breadcrumb per row — no tree, no direct section.
      catalog.tasks.filter(taskVisible).forEach((t) => {
        items.push({ kind: 'task', row: t });
        flat.push({ kind: 'task', key: taskKey(t), row: t, full: t.remainingAmount <= 0 });
      });
      return { plan: items, direct: [] as ProjectTaskRow[], rows: flat };
    }

    (catalog.intermediates ?? []).forEach((n) => {
      const p = planNode(n);
      if (p) { items.push(p); collect(p); }
    });

    // Q8 — direct (or unassigned) tasks, always after the roots. A link whose
    // intermediate is gone from the tree (API note 3) belongs here too: it is
    // still claimable and would otherwise vanish from the picker.
    const inTree = new Set<number>();
    const mark = (n: ProjectTaskNode): void => {
      inTree.add(n.intermediateID);
      (n.intermediates ?? []).forEach(mark);
    };
    (catalog.intermediates ?? []).forEach(mark);
    const directRows = catalog.tasks.filter(
      (t) => (t.intermediateID == null || !inTree.has(t.intermediateID)) && taskVisible(t)
    );
    directRows.forEach((t) => {
      flat.push({ kind: 'task', key: taskKey(t), row: t, full: t.remainingAmount <= 0 });
    });

    return { plan: items, direct: directRows, rows: flat };
  }, [catalog, mode, open, byNode, filter, searching]);

  rowsRef.current = rows;

  // Clamp the cursor whenever the list shrinks under it (a typed term, a collapse).
  useEffect(() => {
    setHl((h) => (h >= rows.length ? rows.length - 1 : h));
  }, [rows.length]);

  // Keep the highlighted line inside the fixed scroll box.
  useEffect(() => {
    if (hl < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>('[data-hl="1"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [hl]);

  // "Show in list" — scroll the picked row into view and put the cursor on it.
  // Token 0 is the initial render, which must not scroll or steal the cursor.
  useEffect(() => {
    if (!revealToken || !value || load !== 'ok') return;
    const idx = rowsRef.current.findIndex((r) => r.kind === 'task' && r.key === value);
    if (idx < 0) return undefined;  // its lot is collapsed, or a term hides it
    setHl(idx);
    // Centre it once the row has painted its data-hl marker. Reusing that
    // marker avoids a second row attribute and any selector-escaping of a key
    // that contains a colon.
    const raf = window.requestAnimationFrame(() => {
      listRef.current
        ?.querySelector<HTMLElement>('[data-hl="1"]')
        ?.scrollIntoView({ block: 'center' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [revealToken, value, load]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const toggleNode = useCallback((id: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const pick = useCallback((row: ProjectTaskRow) => {
    // Q3 — a fully claimed piece is readable but never selectable.
    if (row.remainingAmount <= 0) return;
    onPick(row);
  }, [onPick]);

  const toggleClaims = useCallback((key: string) => {
    setClaimsOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const clearSearch = useCallback(() => {
    setTerm('');
    setHl(-1);
    searchRef.current?.focus();
  }, []);

  // ── Keyboard (Q7) ──────────────────────────────────────────────────────────
  // The contract copied from ProjectPicker: while the panel owns a key it calls
  // BOTH preventDefault and stopPropagation, so EntryModalShell's bubbled
  // keydown never sees it and never saves or cancels behind the picker's back.
  const onKeyDown = useCallback((ev: React.KeyboardEvent) => {
    const inSearch = ev.target === searchRef.current;
    const live = load === 'ok';

    if (ev.key === 'Escape') {
      // Esc clears a typed filter FIRST and only cancels the modal on a second
      // press — a typed filter is the thing people actually want to undo.
      if (term) {
        ev.preventDefault();
        ev.stopPropagation();
        clearSearch();
      }
      return; // an empty search lets Esc bubble to the shell = cancel
    }

    if (!live) return;

    const list = rowsRef.current;

    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      ev.stopPropagation();
      if (!list.length) return;
      setHl((h) => {
        if (h < 0) return ev.key === 'ArrowDown' ? 0 : list.length - 1;
        return Math.max(0, Math.min(list.length - 1, h + (ev.key === 'ArrowDown' ? 1 : -1)));
      });
      return;
    }

    if (ev.key === 'Home' || ev.key === 'End') {
      if (inSearch) return; // Home/End are text navigation while typing
      ev.preventDefault();
      ev.stopPropagation();
      if (list.length) setHl(ev.key === 'Home' ? 0 : list.length - 1);
      return;
    }

    const cur = hl >= 0 ? list[hl] : undefined;

    if ((ev.key === 'ArrowRight' || ev.key === 'ArrowLeft') && cur?.kind === 'node') {
      ev.preventDefault();
      ev.stopPropagation();
      setOpen((prev) => {
        const next = new Set(prev);
        if (ev.key === 'ArrowRight') next.add(cur.id); else next.delete(cur.id);
        return next;
      });
      return;
    }

    if (ev.key === 'Enter') {
      // Enter ALWAYS belongs to the panel while it is open, even with nothing
      // highlighted — otherwise it bubbles to the shell and saves a claim with
      // no task on it.
      ev.preventDefault();
      ev.stopPropagation();
      if (cur?.kind === 'node') { toggleNode(cur.id); return; }
      if (cur?.kind === 'task') { pick(cur.row); return; }
      // Q12's "type code → Enter → Enter": with no cursor, Enter takes the
      // first claimable row the filter left standing.
      const first = list.find((r) => r.kind === 'task' && !r.full);
      if (first && first.kind === 'task') pick(first.row);
      return;
    }

    // Any printable key from the list moves focus to the search field.
    if (!inSearch && ev.key.length === 1 && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
      searchRef.current?.focus();
    }
  }, [load, term, hl, clearSearch, toggleNode, pick]);

  // ── Rendering ──────────────────────────────────────────────────────────────
  const rowIndex = useRef(0);

  const renderTask = (row: ProjectTaskRow): React.ReactNode => {
    const key = taskKey(row);
    const idx = rowIndex.current++;
    const full = row.remainingAmount <= 0;
    const selected = value === key;
    const claimed = row.employeesClaimed?.length ?? 0;
    const showClaims = claimsOpen.has(key);
    const dup = (dups[row.taskID] ?? 0) > 1;
    const justClaimed = justClaimedKey === key;

    const cls = [
      'tp-task',
      selected ? 'sel' : '',
      full ? 'done' : '',
      hl === idx ? 'hl' : '',
      justClaimed ? 'just' : '',
    ].filter(Boolean).join(' ');

    return (
      <React.Fragment key={key}>
        <div
          className={cls}
          role="option"
          aria-selected={selected}
          aria-disabled={full}
          data-hl={hl === idx ? '1' : undefined}
          onMouseDown={(ev) => { ev.preventDefault(); setHl(idx); pick(row); }}
        >
          <span className="chk">{(full || selected) && <Check className="ic" />}</span>
          <span className="code"><Highlight text={row.altTaskID} term={term} /></span>
          <span className="txt">
            <span className="nm">
              {/* Q2 — no account code on the row; it lives in the summary. Flat
                  leads with the description (the name repeats the code column
                  on most tenants), tree leads with the name. */}
              <Highlight
                text={mode === 'flat'
                  ? (row.taskInfo?.description || row.taskInfo?.name)
                  : row.taskInfo?.name}
                term={term}
              />
              {dup && (
                <span className="dual" title="One budget shared across more than one intermediate">
                  <Layers className="ic" />1 budget · {dups[row.taskID]} locations
                </span>
              )}
              {justClaimed && <span className="justm">just claimed</span>}
            </span>
            {mode === 'flat'
              ? <span className="tp-crumb"><Highlight text={row.intermediatePath} term={term} /></span>
              : <span className="desc"><Highlight text={row.taskInfo?.description} term={term} /></span>}
          </span>
          {claimed > 0 ? (
            <button
              type="button"
              className="claims"
              title="Show who claimed"
              aria-expanded={showClaims}
              onMouseDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); toggleClaims(key); }}
            >
              <User className="ic" />{claimed} claimed
              <ChevronRight className={`ic cv${showClaims ? ' open' : ''}`} />
            </button>
          ) : (
            <span className="claims zero">no claims</span>
          )}
          <span className="money">
            {full
              ? <b className="full">Fully claimed</b>
              : <>{money(row.remainingAmount)} <span className="of">of {money(row.originalAmount)}</span></>}
          </span>
        </div>
        {showClaims && (
          <div className="tp-claimwrap">
            <div className="ch">Employees claimed · oldest first</div>
            {(row.employeesClaimed ?? []).map((c, i) => (
              <div className="tp-claim" key={`${c.employeeID}-${c.entryDate}-${i}`}>
                <span className="who">
                  <b>{c.name?.first} {c.name?.last}</b><span>#{c.altEmployeeID}</span>
                </span>
                <span className="amt">{money(c.amountClaimed)}</span>
                <span className="dt">{fmtDate(String(c.entryDate))}</span>
                <span className="wk">{c.weekNumber == null ? '—' : `W${c.weekNumber}`}</span>
              </div>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderItem = (item: PlanItem, depth: number): React.ReactNode => {
    if (item.kind === 'task') return renderTask(item.row);

    const { node, counts } = item;
    const idx = rowIndex.current++;
    const cls = [
      'tp-node',
      item.open ? 'open' : '',
      item.mark === 'context' ? 'ctx' : '',
      item.mark === 'match' ? 'match' : '',
      hl === idx ? 'hl' : '',
    ].filter(Boolean).join(' ');

    return (
      <React.Fragment key={node.intermediateID}>
        <button
          type="button"
          className={cls}
          aria-expanded={item.open}
          data-node={node.intermediateID}
          data-hl={hl === idx ? '1' : undefined}
          onMouseDown={(ev) => { ev.preventDefault(); setHl(idx); toggleNode(node.intermediateID); }}
        >
          <ChevronRight className="ic cv" />
          <span className="lyr"><Layers className="ic" /></span>
          <span className="nm"><Highlight text={node.name} term={term} /></span>
          {node.altIntermediateID
            ? <span className="code"><Highlight text={node.altIntermediateID} term={term} /></span>
            : null}
          {/* Subtree roll-up, not the node's own rows — `totalTasks` on the
              payload is per-node, so this arithmetic is the client's. */}
          <span className={`cnt${counts.total ? '' : ' none'}`}>
            {counts.total ? `${counts.open} of ${counts.total} open` : 'no tasks'}
          </span>
        </button>
        {item.open && (
          <div className="tp-kids" data-d={Math.min(depth + 1, 5)}>
            {item.children.length
              ? item.children.map((c) => renderItem(c, depth + 1))
              : (
                <div className="tp-quiet">
                  No tasks directly under {node.name} — open a child.
                </div>
              )}
          </div>
        )}
      </React.Fragment>
    );
  };

  // The keyboard index must be reassigned from zero on every render pass, in
  // exactly the order `rows` was planned in.
  rowIndex.current = 0;

  const body = (() => {
    if (load === 'loading' || load === 'idle') {
      return (
        <div className="tp-skel" aria-label="Loading tasks">
          {Array.from({ length: 9 }, (_, i) => <i key={i} />)}
        </div>
      );
    }
    if (load === 'error') {
      return (
        <div className="tp-state err">
          <span className="eic"><AlertCircle className="ic" /></span>
          <span className="t">Couldn&rsquo;t load tasks for this project.</span>
          <span className="s">The project is still selected — nothing you typed was lost.</span>
          <button className="tp-btn sm" type="button" onClick={() => setAttempt((a) => a + 1)}>
            <RefreshCw className="ic" /> Try again
          </button>
        </div>
      );
    }
    if (!catalog || catalog.tasks.length === 0) {
      return (
        <div className="tp-state">
          <span className="eic"><Package className="ic" /></span>
          <span className="t">No tasks on this project.</span>
          <span className="s">
            Nothing has been imported for {catalog?.name ?? 'this project'} yet. Add a misc
            claim instead, or pick another project.
          </span>
        </div>
      );
    }
    if (searching && filter.matches === 0) {
      // Deliberately a different state from "no tasks on this project": one
      // says the term is wrong, the other says the project is empty.
      return (
        <div className="tp-state">
          <span className="eic"><Search className="ic" /></span>
          <span className="t">No task matches &ldquo;{term}&rdquo;.</span>
          <span className="s">
            Search covers piece code, description, account code and
            the {mode === 'tree' ? 'intermediate path' : 'breadcrumb'}.
          </span>
          <button className="tp-btn sm" type="button" onClick={clearSearch}>Clear search</button>
        </div>
      );
    }
    if (mode === 'flat') return <>{plan.map((item) => renderItem(item, 0))}</>;
    return (
      <>
        {plan.map((item) => renderItem(item, 0))}
        {direct.length > 0 && (
          <>
            <div className="tp-sect">
              {catalog.hasDirectTasks ? 'Direct tasks' : 'Unassigned tasks'}
              <span className="why">
                {catalog.hasDirectTasks
                  ? 'on the project itself, not under an intermediate'
                  : 'this project isn’t set up for direct tasks — worth checking with the PM'}
              </span>
            </div>
            {direct.map((t) => renderTask(t))}
          </>
        )}
      </>
    );
  })();

  if (projectID == null) {
    return (
      <div className="tp disabled">
        <div className="tp-disabled-msg">
          <AlertCircle className="ic" /> Pick a project first — its tasks load here.
        </div>
      </div>
    );
  }

  const total = catalog?.tasks.length ?? 0;

  return (
    <div className="tp" onKeyDown={onKeyDown}>
      <div className="tp-head">
        <div className="tp-search">
          <Search className="ic" />
          <input
            ref={searchRef}
            value={term}
            onChange={(ev) => { setTerm(ev.target.value); setHl(-1); }}
            placeholder={`Search code, description or ${mode === 'tree' ? 'intermediate' : 'breadcrumb'}`}
            autoComplete="off"
            spellCheck={false}
            aria-label="Search tasks"
            disabled={load !== 'ok'}
          />
          {term && (
            <button className="tp-clear" type="button" aria-label="Clear" onClick={clearSearch}>
              <X className="ic" />
            </button>
          )}
        </div>
        <span className="tp-count" aria-live="polite">
          {searching
            ? <><b>{filter.matches}</b> of {total}</>
            : <><b>{total}</b> task{total === 1 ? '' : 's'}</>}
        </span>
      </div>
      <div className="tp-list" ref={listRef} role="listbox" aria-label="Tasks" tabIndex={-1}>
        {body}
      </div>
    </div>
  );
};

export default TaskPicker;
